'use client'

import { useMandatoryContext } from '@repo/hooks'
import { motion } from 'framer-motion'
import {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import { easeOut, fadeSlideUp } from '../../animations'
import { cn } from '../../utils/cn'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../_base/Dialog'

export type StepId = string

type StepDef = { id: StepId; title?: ReactNode; description?: ReactNode }

type UseMultiStepDialogResponse = ReturnType<typeof useMultiStepDialogHook>

export const MultiStepDialogContext = createContext<UseMultiStepDialogResponse | null>(null)
MultiStepDialogContext.displayName = 'MultiStepDialogContext'

/**
 * React hook that encapsulates the step-navigation logic for an ordered
 * set of dialog screens with dynamic step registration.
 *
 * Its core responsibility is to track which step is currently active,
 * manage step registration, and expose helpers for moving forward,
 * backward, jumping to specific steps, or resetting the flow.
 *
 * @param isOpen - Controls visibility of the dialog
 * @param onOpenChange - Callback triggered when dialog open state changes
 * @param initialStepId - Optional step ID to start with when dialog opens
 * @returns Object containing all step navigation methods and state
 */
export function useMultiStepDialogHook(
  isOpen: boolean,
  onOpenChange?: (open: boolean) => void,
  initialStepId?: StepId
) {
  const [steps, setSteps] = useState<StepDef[]>([])
  const [index, setIndex] = useState(0)
  const hasIndexBeenInitialized = useRef(false)

  const registerStep = useCallback((def: StepDef) => {
    let nextIndex = 0
    setSteps(prevSteps => {
      const stepIndex = prevSteps.findIndex(s => s.id === def.id)
      const existing = prevSteps[stepIndex]

      // if step not found, add it
      if (existing === undefined) {
        nextIndex = prevSteps.length
        return [...prevSteps, def]
      }

      nextIndex = stepIndex

      // if step found, update it if title is different
      if (existing.title === def.title) return prevSteps

      const copy = [...prevSteps]
      copy[stepIndex] = { ...existing, title: def.title, description: def.description }
      return copy
    })
    return nextIndex
  }, [])

  const goTo = useCallback(
    (id: StepId) => {
      setIndex(prevIndex => {
        const stepIndex = steps.findIndex(s => s.id === id)

        if (stepIndex === -1) return prevIndex
        if (stepIndex === prevIndex) return prevIndex

        return stepIndex
      })
    },
    [steps]
  )

  const nextStep = useCallback(() => {
    // clamp next step to be within bounds
    setIndex(prevIndex => Math.min(prevIndex + 1, Math.max(steps.length - 1, 0)))
  }, [steps.length])

  const backStep = useCallback(() => setIndex(prevIndex => Math.max(prevIndex - 1, 0)), [])

  // close dialog
  const close = useCallback(() => onOpenChange?.(false), [onOpenChange])

  // Reset index when dialog opens or jump to initial step
  useEffect(() => {
    if (!isOpen) {
      hasIndexBeenInitialized.current = false
      return
    }

    // This is necessary to make sure the index is only set once when the dialog opens
    // as this effect might run when other dependencies change, such as `steps` caused
    // by a step change causing a re-registration.
    if (hasIndexBeenInitialized.current) return

    if (initialStepId) {
      const initialStepIndex = steps.findIndex(step => step.id === initialStepId)

      if (initialStepIndex !== -1) {
        setIndex(initialStepIndex)
        hasIndexBeenInitialized.current = true
        return
      }

      // Step not found yet - wait for steps to register
      return
    }

    setIndex(0)
    hasIndexBeenInitialized.current = true
  }, [isOpen, initialStepId, steps])

  return {
    registerStep,
    index,
    setIndex,
    nextStep,
    backStep,
    goTo,
    steps,
    isOpen,
    onOpenChange,
    close
  }
}

/**
 * Provider component that couples `useMultiStepDialogHook` with a Radix UI
 * `Dialog`, exposing the hook result via context so that nested components
 * can access navigation helpers through `useMultiStepDialog()`.
 *
 * This component maintains the provider-hook pattern by abstracting all
 * state management into the hook while providing a clean interface for
 * dialog rendering and step management.
 *
 * @param props.isOpen - Controls visibility of the dialog
 * @param props.onOpenChange - Callback triggered when dialog open state changes
 * @param props.modal - Whether the dialog should be modal (default: true)
 * @param props.initialStepId - Optional step ID to start with when dialog opens
 * @param props.closeOnEscape - Whether Escape should close the dialog (default: false)
 * @param props.showCloseButton - Whether to show the close button in the header (default: true)
 */
export function MultiStepDialogProvider({
  isOpen,
  onOpenChange,
  children,
  modal = true,
  initialStepId,
  className,
  dataTestId,
  contentId,
  closeOnEscape = false,
  showCloseButton = true,
  fullScreenMobile,
  onContentDetach
}: PropsWithChildren<{
  isOpen: boolean
  onOpenChange?: (open: boolean) => void
  modal?: boolean
  initialStepId?: StepId
  className?: string
  dataTestId?: string
  contentId?: string
  closeOnEscape?: boolean
  showCloseButton?: boolean
  fullScreenMobile?: boolean
  /** Called when Radix has actually unmounted the dialog content, i.e. after the exit animation finished. Wired to Radix `onCloseAutoFocus`. */
  onContentDetach?: () => void
}>) {
  const hook = useMultiStepDialogHook(isOpen, onOpenChange, initialStepId)
  const title = hook.steps[hook.index]?.title ?? null
  const description = hook.steps[hook.index]?.description ?? null
  const descriptionId = useId()

  return (
    <MultiStepDialogContext.Provider value={hook}>
      <Dialog modal={modal} open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          aria-describedby={description ? descriptionId : undefined}
          className={className}
          closeOnEscape={closeOnEscape}
          data-testid={dataTestId}
          id={contentId}
          fullScreenMobile={fullScreenMobile}
          onCloseAutoFocus={onContentDetach && (() => onContentDetach())}
        >
          <DialogHeader
            className={cn(title === null && description === null && !showCloseButton && 'sr-only')}
            showCloseButton={showCloseButton}
          >
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription id={descriptionId}>{description}</DialogDescription>}
          </DialogHeader>
          {children}
        </DialogContent>
      </Dialog>
    </MultiStepDialogContext.Provider>
  )
}

/**
 * Convenience hook that consumes the `MultiStepDialogContext`.
 *
 * Will throw (via `useMandatoryContext`) if used outside of
 * `MultiStepDialogProvider`, ensuring consumers always have access to the
 * navigation helpers and current step details.
 */
export const useMultiStepDialog = (): UseMultiStepDialogResponse =>
  useMandatoryContext(MultiStepDialogContext, 'MultiStepDialog')

/**
 * Null-tolerant variant of `useMultiStepDialog` for components that render
 * both inside and outside a multi-step dialog (e.g. a back button shared by
 * dialog flows and page-inline flows). Returns `null` when no provider is
 * mounted instead of throwing.
 */
export const useMultiStepDialogOptional = (): UseMultiStepDialogResponse | null =>
  useContext(MultiStepDialogContext)

/**
 * Represents a single screen within a multi-step dialog.
 *
 * NOTE: This type is intentionally minimal – it *only* describes what
 * should be shown (`component`) and the optional heading to render in the
 * dialog chrome (`title`). All other concerns (form state, side-effects,
 * data fetching, etc.) must be handled externally.
 */
export function Step({
  id,
  title,
  description,
  children,
  keepMounted = false // optional: render but hide instead of unmounting
}: PropsWithChildren<{
  id: StepId
  title?: ReactNode
  description?: ReactNode
  keepMounted?: boolean
}>) {
  const { registerStep, index, steps } = useMultiStepDialog()

  // register on mount
  // ensures the step is registered before first paint
  useLayoutEffect(() => {
    registerStep({ id, title, description })
  }, [id, title, description, registerStep])

  const myIndex = steps.findIndex(s => s.id === id)
  const active = myIndex === index

  if (!keepMounted && !active) return null
  return (
    // `min-w-0` lets this step (a grid item of the DialogContent grid) shrink to
    // the dialog width instead of taking its content's min-content, so long
    // unbroken content truncates inside the modal rather than overflowing it.
    <div className="min-w-0" hidden={!active && keepMounted} data-active={active || undefined}>
      {active ? (
        <motion.div {...fadeSlideUp} transition={{ duration: 0.18, ease: easeOut }}>
          {children}
        </motion.div>
      ) : keepMounted ? (
        <div>{children}</div>
      ) : null}
    </div>
  )
}

export function StepIndicator({ barWidth = 'w-12' }: { barWidth?: string } = {}) {
  const { index, steps } = useMultiStepDialog()

  if (!steps.length) return null

  return (
    <div className="flex items-center gap-3">
      <span className="text-secondary-foreground text-3xs tabular-nums">
        {index + 1} / {steps.length}
      </span>
      <div className="flex gap-1.5">
        {steps.map((_, i) => (
          <div
            key={i}
            className={cn(
              'h-1 rounded-full transition-colors',
              barWidth,
              i <= index ? 'bg-foreground' : 'bg-border'
            )}
          />
        ))}
      </div>
    </div>
  )
}
